import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  MobileStateQuestion,
  SaveDataAnswerGroup,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { useEffect, useMemo, useState } from 'react';
import { DateFormat } from '../../constants';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { debounce } from '../../utils';
import {
  MobileDatePicker,
  DesktopDateTimePicker,
  renderMultiSectionDigitalClockTimeView,
} from '@mui/x-date-pickers';
import { Platform } from '../../constants/platform';

dayjs.extend(customParseFormat);

interface CustomDatePickerProps {
  question: MobileStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  disabled?: boolean;
  stringMapper: (key: string) => string;
  onSetAnswer: (questionKey: string, answers: string[]) => void;
  showTile?: boolean;
}

const CustomDatePicker = ({
  question,
  answers,
  stringMapper,
  onSetAnswer,
  disabled,
  showTile,
}: CustomDatePickerProps) => {
  const isDateTime = question.Type === 'DateTime';
  // Field display/typing format (AM/PM for DateTime).
  const displayFormat = isDateTime
    ? DateFormat.DATE_TIME_YYYY_MM_DD_HH_MM_a
    : DateFormat.default;
  // Persisted answer format: Date-only stays MM/DD/YYYY; DateTime saves as
  // 24-hour YYYY-MM-DD HH:mm:ss (e.g. 2026-05-28 05:55:00).
  const saveFormat = isDateTime
    ? DateFormat.DATE_TIME_SAVE
    : DateFormat.default;

  const [value, setValue] = useState<Dayjs | null>(null);

  const dateValidation = {
    maximum: question?.DateRange?.Max
      ? dayjs(question.DateRange.Max)
      : dayjs().add(1, 'year'),
    minimum: question?.DateRange?.Min
      ? dayjs(question.DateRange.Min)
      : dayjs('2024-01-01'),
  };

  const label = useMemo(() => {
    if (!showTile) {
      return '';
    }
    if (stringMapper) {
      return stringMapper(question?.Title);
    }
    return question?.Title;
  }, [showTile, question, stringMapper]);

  useEffect(() => {
    const stored = answers?.Answers?.[0]?.Answer;
    if (typeof stored !== 'string' || stored.trim() === '') {
      setValue(null);
      return;
    }

    const storedAnswer = stored.trim();
    // Existing answers may be stored in the save format, the display format,
    // or as an ISO/Date string; try the explicit formats first, then fall back.
    const bySave = dayjs(storedAnswer, saveFormat, true);
    const byDisplay = dayjs(storedAnswer, displayFormat, true);
    const byFallback = dayjs(storedAnswer);
    setValue(
      bySave.isValid()
        ? bySave
        : byDisplay.isValid()
          ? byDisplay
          : byFallback.isValid()
            ? byFallback
            : null
    );
  }, [answers, saveFormat, displayFormat]);

  const onSelect = debounce((selected: Dayjs | null) => {
    if (!selected || !selected.isValid()) {
      return;
    }
    setValue(selected);
    if (onSetAnswer) {
      onSetAnswer(question.QuestionKey, [selected.format(saveFormat)]);
    }
  }, 10);

  const paperSx = {
    height: '400px',
    position: 'absolute' as const,
    left: '0',
    transform: 'translateX(300px)',
    // Hide the toolbar section with date display
    '& .MuiPickersToolbar-root.MuiDatePickerToolbar-root': {
      display: 'none',
    },
    '& .MuiPickersCalendarHeader-root': {
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    '& .MuiDayPicker-header': {
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    '& .MuiDayPicker-monthContainer': {
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  };

  const dateTimePaperSx = {
    '& .MuiPickersToolbar-root': {
      display: 'none',
    },
    '& .MuiPickersCalendarHeader-root': {
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    // Give the digital hour/minute/AM-PM list a usable height.
    '& .MuiMultiSectionDigitalClock-root': {
      maxHeight: '300px',
    },
  };

  const textFieldSlot = { sx: { cursor: 'pointer' } };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DemoContainer components={['DatePicker']}>
        {isDateTime ? (
          <DesktopDateTimePicker
            ampm
            disabled={disabled}
            label={Platform?.isAudit() ? label : null}
            format={displayFormat}
            onAccept={onSelect}
            minDate={dateValidation.minimum}
            maxDate={dateValidation.maximum}
            value={value && value.isValid() ? value : null}
            views={['year', 'day', 'hours', 'minutes']}
            viewRenderers={{
              hours: renderMultiSectionDigitalClockTimeView,
              minutes: renderMultiSectionDigitalClockTimeView,
            }}
            closeOnSelect={false}
            // Desktop variant also lets the user type the time inline in the
            // field (hh:mm AM/PM)
            slotProps={{
              desktopPaper: { sx: dateTimePaperSx },
              textField: textFieldSlot,
              actionBar: { actions: ['cancel', 'accept'] },
            }}
          />
        ) : (
          <MobileDatePicker
            disabled={disabled}
            label={Platform?.isAudit() ? label : null}
            onAccept={onSelect}
            minDate={dateValidation.minimum}
            maxDate={dateValidation.maximum}
            value={value && value.isValid() ? value : null}
            slotProps={{
              mobilePaper: { sx: paperSx },
              textField: textFieldSlot,
            }}
          />
        )}
      </DemoContainer>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
